export const CANVAS_MIME = 'application/vnd.orqforge.canvas+json';

export type CanvasTone = 'ok' | 'warn' | 'bad';

export interface CanvasKpi {
  label: string;
  value: string;
  tone?: CanvasTone;
}

export interface CanvasTableBlock {
  type: 'table';
  title?: string;
  columns: string[];
  rows: (string | number)[][];
}

export interface CanvasTimelineBlock {
  type: 'timeline';
  title?: string;
  events: { time: string; actor: string; text: string }[];
}

export interface CanvasMarkdownBlock {
  type: 'markdown';
  markdown: string;
}

export interface CanvasStatusBlock {
  type: 'status';
  label: string;
  value: string;
  tone: CanvasTone;
}

export interface CanvasActionItem {
  label: string;
  toolKey: string;
  arguments?: Record<string, unknown>;
  confirm?: string;
}

export interface CanvasActionsBlock {
  type: 'actions';
  title?: string;
  items: CanvasActionItem[];
}

export type CanvasBlock =
  | CanvasTableBlock
  | CanvasTimelineBlock
  | CanvasMarkdownBlock
  | CanvasStatusBlock
  | CanvasActionsBlock;

export interface CanvasDocument {
  version: 1;
  title: string;
  subtitle?: string;
  kpis?: CanvasKpi[];
  blocks: CanvasBlock[];
}

export function isCanvasDocument(value: unknown): value is CanvasDocument {
  if (!value || typeof value !== 'object') return false;
  const doc = value as Record<string, unknown>;
  return doc.version === 1 && typeof doc.title === 'string' && Array.isArray(doc.blocks);
}

export function canvasDocumentFromToolInput(input: Record<string, unknown>): CanvasDocument {
  if (input.document && isCanvasDocument(input.document)) return input.document;
  const blocks = Array.isArray(input.blocks) ? (input.blocks as CanvasBlock[]) : [];
  const kpis = Array.isArray(input.kpis) ? (input.kpis as CanvasKpi[]) : undefined;
  const title = typeof input.title === 'string' && input.title.trim() ? input.title : 'Canvas';
  const subtitle = typeof input.subtitle === 'string' ? input.subtitle : undefined;
  return { version: 1, title, subtitle, kpis, blocks };
}

export function parseCanvasJson(raw: string): CanvasDocument | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    return isCanvasDocument(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function canvasFromToolResult(result: unknown): CanvasDocument | null {
  if (!result || typeof result !== 'object') return null;
  const rec = result as Record<string, unknown>;
  if (isCanvasDocument(rec.canvas)) return rec.canvas;
  const canvases = rec.canvases;
  if (canvases && typeof canvases === 'object') {
    const bag = canvases as Record<string, unknown>;
    if (isCanvasDocument(bag.inbox)) return bag.inbox;
    if (isCanvasDocument(bag.payment)) return bag.payment;
  }
  return isCanvasDocument(result) ? result : null;
}
